import { Component, EventEmitter, OnInit, OnDestroy, Output, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface ModuleForm {
  name: string;
  has_read: boolean;
  has_create: boolean;
  has_update: boolean;
  has_delete: boolean;
  // Included raw selection map for sidebar preview/fine-grained control
  permissions: { [code: string]: boolean };
}

export interface PermissionItem {
  name: string;
  code: string;
  description: string;
}

export interface ModuleDefinition {
  name: string;
  icon: string;
  permissions: PermissionItem[];
}

@Component({
  selector: 'app-permission-selector',
  standalone: false,
  templateUrl: './permission-selector.component.html',
  styleUrls: ['./permission-selector.component.scss'],
})
export class PermissionSelectorComponent implements OnInit, OnDestroy, OnChanges {
  @Input() selectedTemplate: string = '';
  @Input() readOnly: boolean = false;
  @Input() initialPermissions: { [code: string]: boolean } = {};
  @Output() modulesChange = new EventEmitter<ModuleForm[]>();
  @Output() totalPermissionsCount = new EventEmitter<number>();
  @Output() moduleCountsChange = new EventEmitter<{ [name: string]: { granted: number, total: number } }>();
  @Output() permissionDiff = new EventEmitter<{ added: string[]; removed: string[] }>();

  form: FormGroup;
  private destroy$ = new Subject<void>();

  readonly MODULES_DEF: ModuleDefinition[] = [
    {
      name: 'User Management',
      icon: 'users',
      permissions: [
        { name: 'View users', code: 'users.view', description: 'See user list and basic profile' },
        { name: 'Create users', code: 'users.create', description: 'Add new users to the platform' },
        { name: 'Edit users', code: 'users.update', description: 'Modify user details and assignments' },
        { name: 'Delete users', code: 'users.delete', description: 'Permanently remove users' },
        { name: 'Suspend users', code: 'users.suspend', description: 'Temporarily disable user access' }
      ]
    },
    {
      name: 'Roles & Permissions',
      icon: 'shield',
      permissions: [
        { name: 'View roles', code: 'roles.view', description: 'View available roles and permissions' },
        { name: 'Create roles', code: 'roles.create', description: 'Add new custom roles to the system' },
        { name: 'Edit roles', code: 'roles.update', description: 'Edit role names and permission matrix' },
        { name: 'Delete roles', code: 'roles.delete', description: 'Delete unused custom roles' }
      ]
    },
    {
      name: 'Geographic Nodes',
      icon: 'pin',
      permissions: [
        { name: 'View nodes', code: 'nodes.view', description: 'See geographical hierarchy and nodes' },
        { name: 'Create nodes', code: 'nodes.create', description: 'Add new geographical nodes' },
        { name: 'Edit nodes', code: 'nodes.update', description: 'Modify node names and links' },
        { name: 'Delete nodes', code: 'nodes.delete', description: 'Remove geographical nodes' }
      ]
    },
    {
      name: 'Channels',
      icon: 'channel',
      permissions: [
        { name: 'View channels', code: 'channels.view', description: 'See active communication channels' },
        { name: 'Create channels', code: 'channels.create', description: 'Add new communication channels' },
        { name: 'Edit channels', code: 'channels.update', description: 'Configure channel parameters' },
        { name: 'Manage keys', code: 'channels.keys', description: 'View and update channel access keys' }
      ]
    },
    {
      name: 'POS',
      icon: 'pin',
      permissions: [
        { name: 'View POS', code: 'pos.view', description: 'See active POS' },
        { name: 'Create POS', code: 'pos.create', description: 'Onboard new POS records' },
        { name: 'Edit POS', code: 'pos.update', description: 'Modify POS details and approvals' },
        { name: 'Delete POS', code: 'pos.delete', description: 'Remove POS records' },
        { name: 'Merge POS', code: 'pos.merge', description: 'Review and merge duplicate POS records' }
      ]
    },
    {
      name: 'SKU Management',
      icon: 'sku_rules',
      permissions: [
        { name: 'View SKUs', code: 'skus.view', description: 'See SKU catalog and rules' },
        { name: 'Create SKUs', code: 'skus.create', description: 'Add new SKUs to the catalog' },
        { name: 'Edit SKUs', code: 'skus.update', description: 'Modify SKU details and rules' },
        { name: 'Delete SKUs', code: 'skus.delete', description: 'Remove SKUs from the catalog' }
      ]
    },
    {
      name: 'Surveys',
      icon: 'surveys',
      permissions: [
        { name: 'View surveys', code: 'surveys.view', description: 'See survey templates' },
        { name: 'Create surveys', code: 'surveys.create', description: 'Create survey templates' },
        { name: 'Edit surveys', code: 'surveys.update', description: 'Create new versions of survey templates' },
        { name: 'Delete surveys', code: 'surveys.delete', description: 'Retire survey templates' }
      ]
    },
    {
      name: 'Dispatch & Operations',
      icon: 'dispatch',
      permissions: [
        { name: 'View dispatch', code: 'dispatch.view', description: 'See operational task board' },
        { name: 'Assign tasks', code: 'dispatch.assign', description: 'Allocate tasks to agents · requires dispatch.view' },
        { name: 'Close tasks', code: 'dispatch.close', description: 'Mark tasks complete or canceled · requires dispatch.view' }
      ]
    },
    {
      name: 'Reports & Analytics',
      icon: 'reports',
      permissions: [
        { name: 'View reports', code: 'reports.view', description: 'Generate and view system reports' },
        { name: 'Export data', code: 'reports.export', description: 'Download report data in CSV/PDF format' }
      ]
    },
    {
      name: 'System Settings',
      icon: 'settings',
      permissions: []
    }
  ];

  // Accordion state tracker
  expandedModules: { [key: string]: boolean } = {
    'User Management': true,
    'Dispatch & Operations': true
  };

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      modules: this.fb.array([])
    });
  }

  get modules(): FormArray {
    return this.form.get('modules') as FormArray;
  }

  getGrantedCount(): number {
    let count = 0;
    this.modules.controls.forEach(control => {
      const perms = control.get('permissions')?.value || {};
      Object.keys(perms).forEach(k => {
        if (perms[k]) count++;
      });
    });
    return count;
  }

  getTotalPermissionsCount(): number {
    return this.MODULES_DEF.reduce((sum, mod) => sum + mod.permissions.length, 0);
  }

  ngOnInit() {
    this.initializeForm();

    if (Object.keys(this.initialPermissions).length > 0) {
      this.applyInitialPermissions(this.initialPermissions);
    }

    if (this.readOnly) {
      this.form.disable({ emitEvent: false });
    }

    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // Sync legacy flags on every form change.
        // formControlName breaks for keys containing `.` (Angular treats `.` as a
        // path separator), so the (change) event may not fire for dot-keyed permissions.
        // Running updateLegacyFlags here guarantees has_read/has_update stay in sync.
        this.modules.controls.forEach(ctrl => {
          this.updateLegacyFlags(ctrl as FormGroup);
        });
        this.emitChanges();
      });

    this.emitChanges();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedTemplate'] && !changes['selectedTemplate'].isFirstChange()) {
      this.applyTemplate(this.selectedTemplate);
    }
    if (changes['initialPermissions'] && !changes['initialPermissions'].isFirstChange()) {
      this.applyInitialPermissions(this.initialPermissions);
    }
    if (changes['readOnly']) {
      if (this.readOnly) {
        this.form.disable({ emitEvent: false });
      } else {
        this.form.enable({ emitEvent: false });
      }
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForm() {
    const modulesArray = this.modules;
    modulesArray.clear();

    this.MODULES_DEF.forEach(mod => {
      // Build form controls for granular permissions
      const permGroupControls: { [key: string]: any } = {};
      mod.permissions.forEach(p => {
        permGroupControls[p.code] = [false];
      });

      const moduleGroup = this.fb.group({
        name: [mod.name],
        has_read: [false],
        has_create: [false],
        has_update: [false],
        has_delete: [false],
        permissions: this.fb.group(permGroupControls)
      });

      modulesArray.push(moduleGroup);
    });
  }

  // Toggle expansion state
  toggleModule(name: string) {
    this.expandedModules[name] = !this.expandedModules[name];
  }

  // Actions Bar
  expandAll() {
    this.MODULES_DEF.forEach(mod => this.expandedModules[mod.name] = true);
  }

  collapseAll() {
    this.MODULES_DEF.forEach(mod => this.expandedModules[mod.name] = false);
  }

  selectAll() {
    this.modules.controls.forEach(control => {
      const permsGroup = control.get('permissions') as FormGroup;
      Object.keys(permsGroup.controls).forEach(key => {
        permsGroup.controls[key]?.setValue(true);
      });
      this.updateLegacyFlags(control as FormGroup);
    });
  }

  clearAll() {
    this.modules.controls.forEach(control => {
      const permsGroup = control.get('permissions') as FormGroup;
      Object.keys(permsGroup.controls).forEach(key => {
        permsGroup.controls[key]?.setValue(false);
      });
      this.updateLegacyFlags(control as FormGroup);
    });
  }

  // Check if a module is fully selected
  isModuleFullySelected(index: number): boolean {
    const control = this.modules.at(index);
    const permsGroup = control?.get('permissions') as FormGroup;
    if (!permsGroup) return false;
    const controls = Object.values(permsGroup.controls);
    return controls.length > 0 && controls.every(ctrl => ctrl.value === true);
  }

  // Check if a module has custom/partial selection
  isModulePartiallySelected(index: number): boolean {
    const control = this.modules.at(index);
    const permsGroup = control?.get('permissions') as FormGroup;
    if (!permsGroup) return false;
    const controls = Object.values(permsGroup.controls);
    const selectedCount = controls.filter(ctrl => ctrl.value === true).length;
    return selectedCount > 0 && selectedCount < controls.length;
  }

  getModuleGrantedCount(index: number): number {
    const control = this.modules.at(index);
    const permsGroup = control?.get('permissions') as FormGroup;
    if (!permsGroup) return 0;
    const controls = Object.values(permsGroup.controls);
    return controls.filter(ctrl => ctrl.value === true).length;
  }

  getModuleTotalCount(index: number): number {
    const control = this.modules.at(index);
    const permsGroup = control?.get('permissions') as FormGroup;
    if (!permsGroup) return 0;
    return Object.keys(permsGroup.controls).length;
  }

  // Enable/Disable all for a single module
  toggleEnableAllModule(index: number, _event: Event) {
    const isFully = this.isModuleFullySelected(index);
    const targetState = !isFully;

    const control = this.modules.at(index);
    const permsGroup = control.get('permissions') as FormGroup;
    const keys = Object.keys(permsGroup.controls);

    keys.forEach(key => permsGroup.controls[key]?.setValue(targetState));

    this.updateLegacyFlags(control as FormGroup);
    // no need for form.updateValueAndValidity() — setValue() already triggers it
  }

  // Callback when a single checkbox changes
  onPermissionCheckboxChange(moduleIndex: number, permCode: string) {
    const control = this.modules.at(moduleIndex) as FormGroup;
    this.enforceViewPermissionDependency(control, permCode);
    this.updateLegacyFlags(control);

    this.form.updateValueAndValidity();
  }

  private enforceViewPermissionDependency(moduleGroup: FormGroup, changedCode: string) {
    const permsGroup = moduleGroup.get('permissions') as FormGroup;
    if (!permsGroup) return;

    const controlKeys = Object.keys(permsGroup.controls);
    const viewKey = controlKeys.find(key => key.endsWith('.view'));
    if (!viewKey) return;

    const changedVal = permsGroup.controls[changedCode]?.value;

    if (changedCode === viewKey) {
      if (changedVal === false) {
        // If view permission is unchecked, uncheck all other permissions in this module
        controlKeys.forEach(key => {
          if (key !== viewKey) {
            permsGroup.controls[key]?.setValue(false, { emitEvent: false });
          }
        });
      }
    } else {
      if (changedVal === true) {
        // If a non-view permission is checked, ensure view is checked
        permsGroup.controls[viewKey]?.setValue(true, { emitEvent: false });
      }
    }
  }

  // Updates the backward-compatible has_read, has_create, has_update, has_delete columns
  private updateLegacyFlags(moduleGroup: FormGroup) {
    const moduleName = moduleGroup.get('name')?.value;
    const perms = moduleGroup.get('permissions')?.value;

    let hasRead = false;
    let hasCreate = false;
    let hasUpdate = false;
    let hasDelete = false;

    if (moduleName === 'User Management') {
      hasRead = !!perms['users.view'];
      hasCreate = !!perms['users.create'];
      hasUpdate = !!perms['users.update'];
      hasDelete = !!perms['users.delete'] || !!perms['users.suspend'];
    } else if (moduleName === 'Roles & Permissions') {
      hasRead = !!perms['roles.view'];
      hasCreate = !!perms['roles.create'];
      hasUpdate = !!perms['roles.update'];
      hasDelete = !!perms['roles.delete'];
    } else if (moduleName === 'Geographic Nodes') {
      hasRead = !!perms['nodes.view'];
      hasCreate = !!perms['nodes.create'];
      hasUpdate = !!perms['nodes.update'];
      hasDelete = !!perms['nodes.delete'];
    } else if (moduleName === 'Channels') {
      hasRead = !!perms['channels.view'];
      hasCreate = !!perms['channels.create'];
      hasUpdate = !!perms['channels.update'] || !!perms['channels.keys'];
    } else if (moduleName === 'POS') {
      hasRead = !!perms['pos.view'];
      hasCreate = !!perms['pos.create'];
      hasUpdate = !!perms['pos.update'] || !!perms['pos.merge'];
      hasDelete = !!perms['pos.delete'];
    } else if (moduleName === 'SKU Management') {
      hasRead = !!perms['skus.view'];
      hasCreate = !!perms['skus.create'];
      hasUpdate = !!perms['skus.update'];
      hasDelete = !!perms['skus.delete'];
    } else if (moduleName === 'Surveys') {
      hasRead = !!perms['surveys.view'];
      hasCreate = !!perms['surveys.create'];
      hasUpdate = !!perms['surveys.update'];
      hasDelete = !!perms['surveys.delete'];
    } else if (moduleName === 'Dispatch & Operations') {
      hasRead = !!perms['dispatch.view'];
      hasUpdate = !!perms['dispatch.assign'];
      hasDelete = !!perms['dispatch.close'];
    } else if (moduleName === 'Reports & Analytics') {
      hasRead = !!perms['reports.view'];
      hasUpdate = !!perms['reports.export'];
    }

    moduleGroup.get('has_read')?.setValue(hasRead, { emitEvent: false });
    moduleGroup.get('has_create')?.setValue(hasCreate, { emitEvent: false });
    moduleGroup.get('has_update')?.setValue(hasUpdate, { emitEvent: false });
    moduleGroup.get('has_delete')?.setValue(hasDelete, { emitEvent: false });
  }

  // Emits changes to parent
  private emitChanges() {
    const values = this.modules.value as ModuleForm[];
    this.modulesChange.emit(values);

    let count = 0;
    const countsMap: { [name: string]: { granted: number, total: number } } = {};
    const added: string[] = [];
    const removed: string[] = [];

    values.forEach(m => {
      const perms = m.permissions || {};
      const subKeys = Object.keys(perms);
      const selectedSubKeys = subKeys.filter(k => perms[k]);
      count += selectedSubKeys.length;

      countsMap[m.name] = {
        granted: selectedSubKeys.length,
        total: subKeys.length
      };

      if (Object.keys(this.initialPermissions).length > 0) {
        subKeys.forEach(code => {
          const wasGranted = !!this.initialPermissions[code];
          const isGranted = !!perms[code];
          if (!wasGranted && isGranted) added.push(code);
          if (wasGranted && !isGranted) removed.push(code);
        });
      }
    });

    this.totalPermissionsCount.emit(count);
    this.moduleCountsChange.emit(countsMap);
    if (Object.keys(this.initialPermissions).length > 0) {
      this.permissionDiff.emit({ added, removed });
    }
  }

  applyInitialPermissions(permissions: { [code: string]: boolean }) {
    this.modules.controls.forEach(control => {
      const permsGroup = control.get('permissions') as FormGroup;
      Object.keys(permsGroup.controls).forEach(code => {
        permsGroup.controls[code]?.setValue(!!permissions[code], { emitEvent: false });
      });
      this.updateLegacyFlags(control as FormGroup);
    });
    this.form.updateValueAndValidity();
  }

  // Apply templates
  applyTemplate(templateName: string) {
    if (!templateName) return;

    this.modules.controls.forEach(control => {
      const permsGroup = control.get('permissions') as FormGroup;

      Object.keys(permsGroup.controls).forEach(key => {
        let val = false;

        if (templateName === 'Read-only') {
          val = key.endsWith('.view');
        } else if (templateName === 'Region operator') {
          // Viewers, plus node edit, dispatch/tasks assign/close
          val = key.endsWith('.view') ||
            key === 'nodes.update' ||
            key === 'dispatch.assign' ||
            key === 'dispatch.close' ||
            key === 'reports.export';
        } else if (templateName === 'Auditor') {
          val = key.endsWith('.view') ||
            key === 'reports.export';
        } else if (templateName === 'Blank') {
          val = false;
        }

        permsGroup.controls[key]?.setValue(val, { emitEvent: false });
      });

      this.updateLegacyFlags(control as FormGroup);
    });

    this.form.updateValueAndValidity();
  }
}
